import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  EMPTY,
  exhaustMap,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { SurveyService } from '@ps/survey-data-access';
import { Nullable } from '@ps/utils';
import {
  AdvancedFeasibilityFilters,
  ChildSurveysRequest,
  SURVEY_CLASSIFICATION_KIND,
  Survey,
  isLaunchPage,
  SurveyClassification,
  SurveyKind,
  SurveyWorkflowPage,
  UniqueLinksForm,
} from '@ps/survey-utils';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import { SurveyCreationSelectorsFacade } from '../survey-creation.selectors.facade';
import { SurveyCreationInsightsActions } from '../insights/survey-creation-insights.actions';
import { APP_ROUTES } from '@ps/core-config';
import { Router } from '@angular/router';

import {
  SurveyCreationChildrenActions,
  SurveyCreationChildrenApiActions,
  SurveyCreationMcApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenActions,
  SurveyCreationCoreChildrenApiActions,
} from './survey-creation-core.actions';
import {
  selectAdvancedFeasibilityFilters,
  selectAudienceChildren,
} from '../audience/survey-creation-audience.selectors';
import {
  selectActiveChildKey,
  selectCoreChildren,
  selectKind,
  selectSurvey,
  selectSurveyWorkflowPage,
} from './survey-creation-core.selectors';
import { selectLaunchForm } from '../launch/survey-creation-launch.selectors';
import {
  childSurveysRequest,
  redirectsWhenChildless,
} from './utils/child-survey-filter.helper';
import { firstChildSurveyId } from '../audience/utils/audience-children.helper';
import { isLoadedCoreSibling } from './utils/core-children.helper';
import { saveSucceeded$ } from './utils/save-outcome.helper';
import { selectIsSurveySaveable } from './utils/survey-save-pipeline.helper';
import { loadActiveChild$ } from './utils/active-child-load.helper';
import {
  buildChildSwitchContext,
  ChildSwitchViolation,
  validateChildSwitch,
} from './validation/child-switch.validations';
import { ChildSelection, childSelection } from './utils/child-selection.helper';
import { SurveyChildEntry } from '../survey-creation-child-registry.helper';
import { DEFAULT_CORE_CHILD_STATE } from '../survey-creation-state.constants';

@Injectable()
export class SurveyCreationChildrenEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _selectors = inject(SurveyCreationSelectorsFacade);

  public readonly loadChildSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      concatLatestFrom(() => this._store.select(selectCoreChildren)),
      map(([{ survey }, children]) =>
        isLoadedCoreSibling(children, survey.id)
          ? Option.none
          : childSurveysRequest(survey),
      ),
      filter((request) => request.isSome()),
      switchMap((request) => this._loadChildren(request.unwrap())),
    ),
  );

  public readonly redirectOnNoChildren$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess),
        concatLatestFrom(() => this._store.select(selectSurvey)),
        filter(
          ([{ surveys }, survey]) =>
            !surveys.length && !!survey && redirectsWhenChildless(survey),
        ),
        tap(() => this._router.navigateByUrl(APP_ROUTES.DASHBOARD)),
      ),
    { dispatch: false },
  );

  public readonly switchToAddedLocale$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationMcApiActions.addSurveyChildSuccess),
      map(({ survey }) =>
        SurveyCreationCoreChildrenActions.switchChild({ surveyId: survey.id }),
      ),
    ),
  );

  // A project keeps its own id in the route — deleting a locale only moves the
  // active child, the same rule `childSelection` states for picking one. Putting
  // a locale's id in the URL would re-target the whole flow at that locale.
  public readonly reselectAfterDeletedLocale$ = createEffect(() =>
    this._afterDeletedLocale().pipe(
      filter(([, kind]) => kind?.kind === SURVEY_CLASSIFICATION_KIND.GROUP_MC),
      map(([surveyId]) =>
        SurveyCreationCoreActions.setActiveChildKey({
          // The Effect boundary: the action's own `Nullable<SurveyChildKey>` is
          // the meaningful default, since no locale left means no active child.
          activeChildKey: surveyId.unwrapOr(null), // convention-guard-allow
        }),
      ),
    ),
  );

  // A Traditional MC locale is a survey of its own, so the route follows it.
  public readonly routeAfterDeletedLocale$ = createEffect(
    () =>
      this._afterDeletedLocale().pipe(
        filter(
          ([, kind]) => kind?.kind !== SURVEY_CLASSIFICATION_KIND.GROUP_MC,
        ),
        tap(([surveyId, , page]) =>
          this._router.navigateByUrl(
            surveyId.match({
              some: (id) => childUrl(id, page),
              none: () => APP_ROUTES.DASHBOARD,
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  public readonly routeToPickedChild$ = createEffect(
    () =>
      this._switchTo('route').pipe(
        concatLatestFrom(() => this._store.select(selectSurveyWorkflowPage)),
        tap(([surveyId, page]) =>
          this._router.navigateByUrl(childUrl(surveyId, page)),
        ),
      ),
    { dispatch: false },
  );

  public readonly selectPickedChild$ = createEffect(() =>
    this._switchTo('select').pipe(
      concatLatestFrom(() => this._store.select(selectSurvey)),
      exhaustMap(([surveyId, survey]) =>
        survey && SurveyKind.isGroupMcOrTracker(survey)
          ? this._reloadPickedLocale(surveyId)
          : from([
              SurveyCreationCoreActions.setActiveChildKey({
                activeChildKey: surveyId,
              }),
              SurveyCreationInsightsActions.refreshAfterSave(),
            ]),
      ),
    ),
  );

  public readonly updateChildSurveyCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationChildrenActions.updateChildSurveyCpi),
      switchMap(({ surveyId, cpi }) =>
        this._updateChildSurveyCpi(surveyId, cpi),
      ),
    ),
  );

  private _allowsSwitch(
    survey: Option<Survey>,
    advancedFilters: AdvancedFeasibilityFilters,
    uniqueLinks: UniqueLinksForm,
  ): boolean {
    const buyerSettings = this._userSettingsSelectors.buyerSettings();

    return validateChildSwitch(
      survey,
      buildChildSwitchContext({
        advancedFilters,
        isRespondentStatusFilterEnabled:
          !!buyerSettings.enableRespondentStatusFilter,
        isManualProcessingEnabled: !!buyerSettings.enableManualProcessing,
        isFeasibilityLoading: this._selectors.feasibilityLoading(),
        uniqueLinks,
      }),
    ).match({
      ok: () => true,
      err: (violation) => {
        this._notifyViolation(violation);
        return false;
      },
    });
  }

  private _notifyViolation({ message, severity }: ChildSwitchViolation): void {
    if (severity === 'warning') {
      this._toastService.warning(message);
      return;
    }

    this._toastService.error(message);
  }

  // Emits only when the locale just deleted was the active one, paired with the
  // locale that should take its place. The reducers have already dropped the
  // deleted key from both slices by the time an effect sees the action, so the
  // remaining entries are exactly the locales still on the table — both
  // `reselectAfterDeletedLocale$` and `routeAfterDeletedLocale$` depend on that
  // ordering.
  private _afterDeletedLocale(): Observable<
    readonly [
      Option<number>,
      Nullable<SurveyClassification>,
      Option<SurveyWorkflowPage>,
    ]
  > {
    return this._actions$.pipe(
      ofType(SurveyCreationMcApiActions.deleteSurveyChildSuccess),
      concatLatestFrom(() => [
        this._store.select(selectActiveChildKey),
        this._store.select(selectAudienceChildren),
        this._store.select(selectCoreChildren),
        this._store.select(selectKind),
        this._store.select(selectSurveyWorkflowPage),
      ]),
      filter(([{ surveyId }, activeChildKey]) => surveyId === activeChildKey),
      map(
        ([, , children, coreChildren, kind, page]) =>
          [
            firstChildSurveyId(
              Object.entries(children).map(
                ([key, child]): SurveyChildEntry => [
                  key,
                  child,
                  coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
                ],
              ),
            ),
            kind,
            page,
          ] as const,
      ),
    );
  }

  private _switchTo(kind: ChildSelection['kind']): Observable<number> {
    return this._actions$.pipe(
      ofType(SurveyCreationCoreChildrenActions.switchChild),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectAdvancedFeasibilityFilters),
        this._store.select(selectAudienceChildren),
        this._store.select(selectIsSurveySaveable),
        this._store.select(selectLaunchForm),
      ]),
      filter(
        ([{ surveyId }, survey]) =>
          !!survey &&
          survey.id !== surveyId &&
          childSelection(survey, surveyId).kind === kind,
      ),
      // validate before saving or routing.
      filter(
        ([, survey, advancedFilters, , , launchForm]) =>
          !!survey &&
          this._allowsSwitch(
            Option.some(survey),
            advancedFilters,
            launchForm.value.uniqueLinks,
          ),
      ),
      switchMap(([{ surveyId }, survey, , children, isSaveable]) =>
        survey &&
        children[survey.id] &&
        (SurveyKind.isTraditionalMc(survey) ||
          SurveyKind.isGroupMc(survey) ||
          SurveyKind.isTracker(survey))
          ? this._saveThen(surveyId, survey, isSaveable)
          : of(surveyId),
      ),
    );
  }

  private _updateChildSurveyCpi(
    surveyId: number,
    cpi: number,
  ): Observable<Action> {
    const isChildSurvey = true;

    return this._surveyService
      .updateOfferPrice(surveyId, cpi, isChildSurvey)
      .pipe(
        map((survey) =>
          SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess({
            survey,
          }),
        ),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationChildrenApiActions.updateChildSurveyCpiFailure({
                surveyId,
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  private _reloadPickedLocale(surveyId: number): Observable<Action> {
    return loadActiveChild$(
      this._surveyService.getByIdTargeting(surveyId, true),
      this.psCatchError,
      {
        precedingActions: [
          SurveyCreationCoreActions.setActiveChildKey({
            activeChildKey: surveyId,
          }),
        ],
        notify: true,
      },
    );
  }

  private _saveThen(
    surveyId: number,
    survey: Survey,
    isSaveable: boolean,
  ): Observable<number> {
    const isProject =
      SurveyKind.isGroupMc(survey) || SurveyKind.isTracker(survey);
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurvey({ silent: true }),
    );

    if (isProject && !isSaveable) {
      return EMPTY;
    }

    return saveSucceeded$(this._actions$).pipe(map(() => surveyId));
  }

  private _loadChildren({
    filter: childFilter,
    options,
  }: ChildSurveysRequest): Observable<Action> {
    return this._surveyService.getChildSurveys(childFilter, options).pipe(
      map((surveys: Survey[]) =>
        SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess({
          surveys,
        }),
      ),
      this.psCatchError((error) => {
        this._toastService.error(error.message);
        return of(
          SurveyCreationCoreChildrenApiActions.loadChildSurveysFailure({
            error: error.message,
          }),
        );
      }),
    );
  }
}

// A routed child switch stays on the page it was made from.
function childUrl(surveyId: number, page: Option<SurveyWorkflowPage>): string {
  return isLaunchPage(page)
    ? APP_ROUTES.launchSurvey(surveyId)
    : APP_ROUTES.editSurvey(surveyId);
}
